import React from "react";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Trash2, Check, X } from "lucide-react";
import { DocumentChip } from "@shared/consultation/ConsultationDocuments";

// One chat bubble: own/their alignment, hover actions, inline editor, attachment, deleted placeholder.
export function MessageBubble({ m, mine, cid, isCompleted, editing, setEditing, onSaveEdit, onDelete }) {
  return (
    <div className={`group flex w-full max-w-full min-w-0 items-end gap-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`cmsg-${mine ? "mine" : "theirs"}`}>
      {mine && !isCompleted && !m.deleted_at && editing?.id !== m.id && (
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-testid={`cmsg-actions-${m.id}`}>
          {!m.attachment && (
            <button type="button" aria-label="Edit message" data-testid={`cmsg-edit-${m.id}`} onClick={() => setEditing({ id: m.id, content: m.content })}
              className="p-1.5 rounded-lg text-slate-400 hover:text-[#0A2540] hover:bg-slate-100"><Pencil size={13} /></button>
          )}
          <button type="button" aria-label="Delete message" data-testid={`cmsg-delete-${m.id}`} onClick={() => onDelete(m)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={13} /></button>
        </div>
      )}
      <div className={`max-w-[80%] min-w-0 break-words rounded-2xl px-4 py-2.5 text-sm [overflow-wrap:anywhere] ${mine ? "bg-[#0A2540] text-white rounded-tr-sm" : "bg-[#FBF4DF] text-[#0A2540] rounded-tl-sm"}`}>
        {m.deleted_at ? (
          <p className={`italic ${mine ? "text-white/60" : "text-slate-500"}`} data-testid={`cmsg-deleted-${m.id}`}>This message was deleted</p>
        ) : editing?.id === m.id ? (
          <div data-testid={`cmsg-editor-${m.id}`}>
            <Textarea value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSaveEdit(); } if (e.key === "Escape") setEditing(null); }}
              data-testid={`cmsg-edit-input-${m.id}`} rows={2} className="min-h-[40px] text-sm text-[#0A2540] bg-white rounded-lg" />
            <div className="flex justify-end gap-1 mt-1">
              <button type="button" onClick={() => setEditing(null)} aria-label="Cancel edit" data-testid={`cmsg-edit-cancel-${m.id}`} className="p-1 rounded text-white/70 hover:text-white"><X size={14} /></button>
              <button type="button" onClick={onSaveEdit} aria-label="Save edit" data-testid={`cmsg-edit-save-${m.id}`} className="p-1 rounded bg-[#D4AF37] text-[#0A2540]"><Check size={14} /></button>
            </div>
          </div>
        ) : m.attachment ? (
          <>
            <DocumentChip cid={cid} doc={m.attachment} dark={mine} testid={`cmsg-attachment-${m.id}`} />
            {m.content && <p className="whitespace-pre-wrap mt-2" data-testid={`cmsg-caption-${m.id}`}>{m.content}</p>}
          </>
        ) : <p className="whitespace-pre-wrap">{m.content}</p>}
        <p className={`text-[10px] mt-1 ${mine ? "text-white/50" : "text-slate-500"}`}>
          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{m.edited_at && !m.deleted_at ? " · edited" : ""}
        </p>
      </div>
    </div>
  );
}

export function TypingIndicator() {
  return (
    <div className="flex justify-start" data-testid="typing-indicator">
      <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" />
        <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: "120ms" }} />
        <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: "240ms" }} />
      </div>
    </div>
  );
}
